/**
 * AdvanceFilterDrawer — reusable, config-driven filter drawer.
 *
 * Usage pattern (best practice):
 *   1. Define a `filterConfig` array (see FieldConfig below) per domain.
 *   2. Store `filterValues` in the page component (state persists between opens).
 *   3. Pass `values` + `onChange` — this component is fully controlled.
 *   4. `onApply(params, tags)` — parent dispatches to Redux.
 *   5. `onReset()` — parent clears Redux + resets local values.
 *
 * FieldConfig shape:
 *   { key, label, type, placeholder?, options?, loadOptions?,
 *     paramKey?, paramKeys?: [fromKey, toKey], tagName }
 *
 * Supported types: 'text' | 'select' | 'multiselect' | 'date_range' | 'async_select'
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { FaFilter, FaTimes } from 'react-icons/fa';
import { MdSearch, MdOutlineRestartAlt } from 'react-icons/md';
import { format } from 'date-fns';
import Drawer from './Drawer';
import DateRangePicker from './DateRangePicker';

// ── Shared styles ─────────────────────────────────────────────────────────────

import { INPUT_CLS } from 'helpers/formStyles';
import { INPUT_CLS as DATEPICKER_CLS } from 'helpers/formStyles';
const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#818cf8' : '#cbd5e1',
    boxShadow: state.isFocused ? '0 0 0 2px #a5b4fc' : 'none',
    '&:hover': { borderColor: '#818cf8' },
    fontSize: 14,
    minHeight: 34,
  }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: 14 }),
};

// ── Field row ─────────────────────────────────────────────────────────────────

const FieldRow = ({ label, children }) => (
  <div className="tw-flex tw-items-start tw-gap-4">
    <label className="tw-w-36 tw-shrink-0 tw-text-right tw-text-sm tw-font-medium tw-text-slate-600 tw-pt-2">
      {label}
    </label>
    <div className="tw-flex-1 tw-min-w-0">{children}</div>
  </div>
);

FieldRow.propTypes = {
  label:    PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const Divider = () => <hr className="tw-my-3 tw-border-dashed tw-border-slate-200" />;

// ── Field renderers ───────────────────────────────────────────────────────────

const renderField = (field, value, onChange) => {
  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          placeholder={field.placeholder || field.label}
          className={INPUT_CLS}
        />
      );

    case 'select':
      return (
        <Select
          options={field.options || []}
          placeholder="Select..."
          classNamePrefix="react-select"
          styles={SELECT_STYLES}
          isClearable
          value={(field.options || []).find((o) => o.value === value) || null}
          onChange={(sel) => onChange(field.key, sel ? sel.value : '')}
        />
      );

    case 'multiselect':
      return (
        <Select
          options={field.options || []}
          placeholder="Select..."
          classNamePrefix="react-select"
          styles={SELECT_STYLES}
          isMulti
          closeMenuOnSelect={false}
          value={(field.options || []).filter((o) => (value || []).includes(o.value))}
          onChange={(sel) => onChange(field.key, sel ? sel.map((o) => o.value) : [])}
        />
      );

    case 'async_select':
      return (
        <AsyncSelect
          cacheOptions
          defaultOptions
          loadOptions={field.loadOptions}
          placeholder="Search..."
          classNamePrefix="react-select"
          styles={SELECT_STYLES}
          isClearable
          value={value ? { value, label: value } : null}
          onChange={(sel) => onChange(field.key, sel ? sel.value : '')}
        />
      );

    // Async multi-select — stores the full array of { value, label } objects so
    // labels survive without a static options list. Use for large/searchable sets.
    case 'async_multiselect':
      return (
        <AsyncSelect
          cacheOptions
          defaultOptions
          loadOptions={field.loadOptions}
          placeholder="Search..."
          classNamePrefix="react-select"
          styles={SELECT_STYLES}
          isMulti
          isClearable
          closeMenuOnSelect={false}
          value={value || []}
          onChange={(sel) => onChange(field.key, sel || [])}
        />
      );

    case 'date_range': {
      const [startDate, endDate] = value || [null, null];
      return (
        <DatePicker
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={([from, to]) => onChange(field.key, [from, to])}
          isClearable
          dateFormat="dd MMM yyyy"
          placeholderText="Select date range"
          className={DATEPICKER_CLS}
          wrapperClassName="tw-w-full"
          showMonthDropdown
          useShortMonthInDropdown
        />
      );
    }

    case 'date_range_picker': {
      const { startDate, endDate } = value || {};
      return (
        <DateRangePicker
          label={field.placeholder || `Select ${field.label}`}
          startDate={startDate || ''}
          endDate={endDate || ''}
          onChange={(range) => onChange(field.key, range)}
        />
      );
    }

    case 'checkbox':
      return (
        <label className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-mt-0.5">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(field.key, e.target.checked)}
            className="tw-w-4 tw-h-4 tw-accent-primary-600 tw-cursor-pointer"
          />
        </label>
      );

    default:
      return null;
  }
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d) => (d ? format(d, 'yyyy-MM-dd') : undefined);

export const buildFilterParams = (fields, values) => {
  const params = {};
  fields.forEach((field) => {
    const val = values[field.key];
    if (field.type === 'date_range') {
      const [from, to] = val || [null, null];
      if (from) params[field.paramKeys[0]] = fmtDate(from);
      if (to)   params[field.paramKeys[1]] = fmtDate(to);
    } else if (field.type === 'date_range_picker') {
      const { startDate, endDate } = val || {};
      if (startDate) params[field.paramKeys[0]] = startDate;
      if (endDate)   params[field.paramKeys[1]] = endDate;
    } else if (field.type === 'checkbox') {
      if (val === true) params[field.paramKey] = true;
    } else if (field.type === 'async_multiselect') {
      if (Array.isArray(val) && val.length > 0) params[field.paramKey] = val.map((o) => o.value);
    } else if (Array.isArray(val)) {
      if (val.length > 0) params[field.paramKey] = val;
    } else if (val !== '' && val != null && val !== false) {
      params[field.paramKey] = val;
    }
  });
  return params;
};

export const buildFilterTags = (fields, values) => {
  const tags = [];
  fields.forEach((field) => {
    const val = values[field.key];
    if (field.type === 'date_range') {
      const [from, to] = val || [null, null];
      if (from && to)
        tags.push({ id: field.paramKeys[0], name: field.tagName, value: [fmtDate(from), fmtDate(to)] });
    } else if (field.type === 'date_range_picker') {
      const { startDate, endDate } = val || {};
      if (startDate && endDate)
        tags.push({ id: field.paramKeys[0], name: field.tagName, value: [startDate, endDate] });
    } else if (field.type === 'checkbox') {
      if (val === true)
        tags.push({ id: field.paramKey, name: field.tagName, value: 'Yes' });
    } else if (field.type === 'async_multiselect') {
      if (Array.isArray(val) && val.length > 0)
        tags.push({ id: field.paramKey, name: field.tagName, value: val.map((o) => o.label) });
    } else if (Array.isArray(val) && val.length > 0) {
      tags.push({ id: field.paramKey, name: field.tagName, value: val });
    } else if (val !== '' && val != null && val !== false) {
      tags.push({ id: field.paramKey, name: field.tagName, value: val });
    }
  });
  return tags;
};

export const getEmptyValues = (fields) =>
  Object.fromEntries(
    fields.map((f) => [
      f.key,
      f.type === 'date_range'        ? [null, null] :
      f.type === 'date_range_picker' ? { startDate: '', endDate: '' } :
      f.type === 'multiselect'       ? [] :
      f.type === 'async_multiselect' ? [] :
      f.type === 'checkbox'          ? false :
      '',
    ])
  );

const countActiveFilters = (fields, values) =>
  fields.reduce((n, f) => {
    const val = values[f.key];
    if (f.type === 'date_range')        return n + (val?.[0] ? 1 : 0);
    if (f.type === 'date_range_picker') return n + (val?.startDate ? 1 : 0);
    if (f.type === 'checkbox')          return n + (val === true ? 1 : 0);
    if (Array.isArray(val))             return n + (val.length > 0 ? 1 : 0);
    return n + (val ? 1 : 0);
  }, 0);

// ── Main component ────────────────────────────────────────────────────────────

const AdvanceFilterDrawer = ({
  show,
  onClose,
  onApply,
  onReset,
  fields,
  values,
  onChange,
  title = 'Advanced filters',
  description,
  width = '460px',
  dividerAfter = [],
  children,
  extraActiveCount = 0,
}) => {
  const fieldActiveCount = useMemo(() => countActiveFilters(fields, values), [fields, values]);
  const activeCount = fieldActiveCount + extraActiveCount;

  const handleApply = () => {
    const params = buildFilterParams(fields, values);
    const tags   = buildFilterTags(fields, values);
    onApply(params, tags);
    onClose();
  };

  const handleReset = () => {
    onReset();
  };

  const footer = (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
      <span className="tw-text-xs tw-text-slate-400">
        {activeCount > 0 ? `${activeCount} filter${activeCount > 1 ? 's' : ''} active` : 'No filters applied'}
      </span>
      <div className="tw-flex tw-gap-2">
        <button
          type="button"
          onClick={handleReset}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-md tw-bg-red-50 tw-text-red-600 tw-border tw-border-red-200 hover:tw-bg-red-100 tw-transition-colors tw-cursor-pointer"
        >
          <MdOutlineRestartAlt size={15} /> Reset
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-md tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-transition-colors tw-cursor-pointer tw-border-0"
        >
          <MdSearch size={15} /> Apply
        </button>
      </div>
    </div>
  );

  return (
    <Drawer
      show={show}
      onHide={onClose}
      backdropClick={false}
      width={width}
      minWidth="380px"
      title={
        <span className="tw-flex tw-items-center tw-gap-2">
          <FaFilter size={13} className="tw-text-primary-500" />
          {title}
          {activeCount > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-primary-600 tw-text-white tw-text-xs tw-font-bold">
              {activeCount}
            </span>
          )}
        </span>
      }
      footer={footer}
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        {/* Description */}
        {description && (
          <div className="tw-flex tw-items-start tw-gap-2 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-md tw-px-4 tw-py-3 tw-text-sm tw-text-blue-700 tw-mb-2">
            {description}
          </div>
        )}

        {/* Active filter chips */}
        {activeCount > 0 && (
          <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-pb-1">
            {fields.map((field) => {
              const val = values[field.key];
              const hasValue =
                field.type === 'date_range'        ? val?.[0] :
                field.type === 'date_range_picker' ? !!val?.startDate :
                field.type === 'checkbox'          ? val === true :
                Array.isArray(val)                 ? val.length > 0 :
                !!val;
              if (!hasValue) return null;

              const display =
                field.type === 'date_range'        ? `${fmtDate(val[0])} → ${fmtDate(val[1])}` :
                field.type === 'date_range_picker' ? `${val.startDate} → ${val.endDate}` :
                field.type === 'checkbox'          ? 'Yes' :
                field.type === 'async_multiselect' ? val.map((o) => o.label).join(', ') :
                Array.isArray(val)                 ? val.join(', ') :
                String(val);

              return (
                <span
                  key={field.key}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-bg-primary-50 tw-border tw-border-primary-200 tw-text-primary-700 tw-rounded-xl tw-text-xs"
                >
                  <span className="tw-font-medium">{field.tagName}:</span>
                  <span className="tw-truncate tw-max-w-[120px]">{display}</span>
                  <button
                    type="button"
                    onClick={() => onChange(field.key,
                      field.type === 'date_range'        ? [null, null] :
                      field.type === 'date_range_picker' ? { startDate: '', endDate: '' } :
                      field.type === 'multiselect'       ? [] :
                      field.type === 'async_multiselect' ? [] :
                      field.type === 'checkbox'          ? false :
                      ''
                    )}
                    className="tw-text-primary-400 hover:tw-text-primary-700 tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer tw-leading-none"
                  >
                    <FaTimes size={9} />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* Fields */}
        {fields.map((field) => (
          <React.Fragment key={field.key}>
            <FieldRow label={field.label}>
              {renderField(field, values[field.key], onChange)}
            </FieldRow>
            {dividerAfter.includes(field.key) && <Divider />}
          </React.Fragment>
        ))}

        {/* Optional extra content (e.g. a dynamic-condition builder) */}
        {children && (
          <>
            <Divider />
            {children}
          </>
        )}
      </div>
    </Drawer>
  );
};

AdvanceFilterDrawer.propTypes = {
  show:         PropTypes.bool.isRequired,
  onClose:      PropTypes.func.isRequired,
  onApply:      PropTypes.func.isRequired,
  onReset:      PropTypes.func.isRequired,
  fields:       PropTypes.array.isRequired,
  values:       PropTypes.object.isRequired,
  onChange:     PropTypes.func.isRequired,
  title:        PropTypes.string,
  description:  PropTypes.string,
  width:        PropTypes.string,
  dividerAfter: PropTypes.arrayOf(PropTypes.string),
  children:     PropTypes.node,
  extraActiveCount: PropTypes.number,
};

export default AdvanceFilterDrawer;
